import {
  Container,
  Title,
  Text,
  Stack,
  Table,
  Badge,
  Alert,
  Collapse,
  Group,
  ActionIcon,
  Paper,
  LoadingOverlay,
  Box,
} from "@mantine/core";
import { useState } from "react";
import {
  IconInfoCircle,
  IconChevronDown,
  IconChevronRight,
  IconArrowRight,
} from "@tabler/icons-react";
import {
  useWorkflowsAdmin,
  useWorkflowStatusesAdmin,
} from "../../../hooks/admin/useWorkflowsAdmin";
import {
  Workflow,
  WorkflowCaseStatus,
} from "../../../services/workflows.service";
import { formatWorkflowType } from "../../../utils/formatters";

interface ExpandableWorkflowRowProps {
  workflow: Workflow;
  isExpanded: boolean;
  onToggle: () => void;
}

function ExpandableWorkflowRow({
  workflow,
  isExpanded,
  onToggle,
}: ExpandableWorkflowRowProps) {
  const { data: statuses = [], isLoading } = useWorkflowStatusesAdmin(
    isExpanded ? workflow.id : undefined
  );

  const workflowType = workflow.accessioningType || "";
  const isPassThrough = workflowType === "pass_through";

  // Find initial and final statuses from the actual data
  const initialStatus = statuses.find((s) => s.isInitialStatus);
  const finalStatus = statuses.find((s) => s.isFinalStatus);

  // Get the status name from caseStatus relation or fallback
  const getStatusName = (status: WorkflowCaseStatus) => {
    return status.caseStatus?.name || status.name || "Unknown";
  };

  return (
    <>
      <Table.Tr style={{ cursor: "pointer" }} onClick={onToggle}>
        <Table.Td>
          <ActionIcon variant="subtle" size="sm">
            {isExpanded ? (
              <IconChevronDown size={16} />
            ) : (
              <IconChevronRight size={16} />
            )}
          </ActionIcon>
        </Table.Td>
        <Table.Td>
          <Badge variant="light" color="blue" size="lg">
            {formatWorkflowType(workflowType)}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{workflow.description || "—"}</Text>
        </Table.Td>
        <Table.Td>
          <Badge variant="outline" color="gray">
            {workflow.statusCount ?? "—"} steps
          </Badge>
        </Table.Td>
      </Table.Tr>

      {/* Expanded content */}
      <Table.Tr>
        <Table.Td
          colSpan={4}
          p={0}
          style={{ border: isExpanded ? undefined : "none" }}
        >
          <Collapse in={isExpanded}>
            <Box p="md" bg="gray.0">
              <Stack gap="md">
                {/* Workflow info */}
                <Group gap="xl">
                  <div>
                    <Text size="xs" c="dimmed">
                      Initial Status
                    </Text>
                    <Badge
                      color={isPassThrough ? "orange" : "green"}
                      variant="light"
                    >
                      {isPassThrough
                        ? "Conditional"
                        : initialStatus
                        ? getStatusName(initialStatus)
                        : "Unknown"}
                    </Badge>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">
                      Final Status
                    </Text>
                    <Badge color="red" variant="light">
                      {finalStatus ? getStatusName(finalStatus) : "Unknown"}
                    </Badge>
                  </div>
                </Group>

                {/* Conditional routing note for pass_through */}
                {isPassThrough && (
                  <Alert
                    icon={<IconInfoCircle size={16} />}
                    color="orange"
                    variant="light"
                    title="Conditional Routing"
                  >
                    <Text size="sm">
                      Pass-Through uses conditional routing based on slide
                      status:
                    </Text>
                    <Stack gap={4} mt="xs">
                      <Text size="sm">
                        • <strong>Pending Cutting</strong> — if any slide is
                        incomplete
                      </Text>
                      <Text size="sm">
                        • <strong>Pending Distribution</strong> — if all slides
                        are complete
                      </Text>
                    </Stack>
                  </Alert>
                )}

                {/* Status flow visualization */}
                <div>
                  <Text size="sm" fw={500} mb="xs">
                    Status Flow
                  </Text>
                  {isLoading ? (
                    <Box pos="relative" h={50}>
                      <LoadingOverlay visible />
                    </Box>
                  ) : (
                    <Group gap="xs" wrap="wrap">
                      {statuses.map((status, index) => (
                        <Group key={status.id} gap="xs">
                          <Badge
                            variant={
                              status.isInitialStatus || status.isFinalStatus
                                ? "filled"
                                : "light"
                            }
                            color={
                              status.isInitialStatus
                                ? "green"
                                : status.isFinalStatus
                                ? "red"
                                : "blue"
                            }
                          >
                            {getStatusName(status)}
                          </Badge>
                          {index < statuses.length - 1 && (
                            <IconArrowRight size={14} color="gray" />
                          )}
                        </Group>
                      ))}
                    </Group>
                  )}
                </div>

                {/* Status details table */}
                <div>
                  <Text size="sm" fw={500} mb="xs">
                    Status Details
                  </Text>
                  <Paper withBorder>
                    <Table>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th w={80}>Order</Table.Th>
                          <Table.Th>Status</Table.Th>
                          <Table.Th w={100}>Notes</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {isLoading ? (
                          <Table.Tr>
                            <Table.Td colSpan={3}>
                              <Text size="sm" c="dimmed" ta="center">
                                Loading...
                              </Text>
                            </Table.Td>
                          </Table.Tr>
                        ) : statuses.length === 0 ? (
                          <Table.Tr>
                            <Table.Td colSpan={3}>
                              <Text size="sm" c="dimmed" ta="center">
                                No statuses configured
                              </Text>
                            </Table.Td>
                          </Table.Tr>
                        ) : (
                          statuses.map((status) => (
                            <Table.Tr key={status.id}>
                              <Table.Td>
                                <Text size="sm">{status.orderSequence}</Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">{getStatusName(status)}</Text>
                              </Table.Td>
                              <Table.Td>
                                {status.isInitialStatus && (
                                  <Badge
                                    size="xs"
                                    color="green"
                                    variant="light"
                                  >
                                    Initial
                                  </Badge>
                                )}
                                {status.isFinalStatus && (
                                  <Badge size="xs" color="red" variant="light">
                                    Terminal
                                  </Badge>
                                )}
                              </Table.Td>
                            </Table.Tr>
                          ))
                        )}
                      </Table.Tbody>
                    </Table>
                  </Paper>
                </div>
              </Stack>
            </Box>
          </Collapse>
        </Table.Td>
      </Table.Tr>
    </>
  );
}

export default function WorkflowsAdminPage() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { data: workflows = [], isLoading } = useWorkflowsAdmin();

  const handleToggle = (workflowId: string) => {
    setExpandedId(expandedId === workflowId ? null : workflowId);
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={2}>Workflows</Title>
          <Text size="sm" c="dimmed" mt="xs">
            System-defined workflows mapping case types to status sequences.
            These values are read-only.
          </Text>
        </div>

        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          Each workflow defines the sequence of statuses a case moves through
          based on its accessioning type. Click a row to see the full status
          sequence.
        </Alert>

        {isLoading ? (
          <Box pos="relative" h={200}>
            <LoadingOverlay visible />
          </Box>
        ) : (
          <Paper withBorder>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={50} />
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th w={100}>Steps</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {workflows.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={4}>
                      <Text size="sm" c="dimmed" ta="center" py="xl">
                        No workflows found
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  workflows.map((workflow) => (
                    <ExpandableWorkflowRow
                      key={workflow.id}
                      workflow={workflow}
                      isExpanded={expandedId === workflow.id}
                      onToggle={() => handleToggle(workflow.id)}
                    />
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Paper>
        )}

        <Text size="xs" c="dimmed">
          Showing {workflows.length} workflows
        </Text>
      </Stack>
    </Container>
  );
}
